import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion, useScroll } from 'framer-motion';
import Icon, { WhatsAppIcon } from '../components/Icon';
import { PageHero, SectionHead, ArrowButton } from '../components/ui';
import { Reveal, Stagger, StaggerItem, EASE } from '../motion/Reveal';
import { API_BASE } from '../config';
import { CONTACT, PROCESS_STEPS, FORM_OPTIONS } from '../data/site';

const EMPTY = { name: '', email: '', phone: '', company: '', projectType: '', projectLocation: '', projectScale: '', timeline: '', message: '' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(f) {
  const e = {};
  if (!f.name.trim()) e.name = 'Please enter your name.';
  if (!EMAIL_RE.test(f.email.trim())) e.email = 'Please enter a valid email address.';
  if (f.phone.replace(/\D/g, '').length < 10) e.phone = 'Please enter a valid phone number.';
  return e;
}

function SuccessCheck() {
  return (
    <svg className="check" viewBox="0 0 96 96" aria-hidden="true">
      <motion.circle cx="48" cy="48" r="44" fill="#DCFCE7" stroke="#16A34A" strokeWidth="3"
        initial={{ pathLength: 0, scale: 0.6 }} animate={{ pathLength: 1, scale: 1 }} transition={{ duration: 0.8, ease: EASE }} />
      <motion.path d="M30 49 l12 12 l24 -26" fill="none" stroke="#16A34A" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.6, duration: 0.5, ease: EASE }} />
    </svg>
  );
}

function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.8', 'start 0.3'] });
  return (
    <section className="section section-alt">
      <div className="container">
        <SectionHead center num="03" kicker="What happens next" title="From brief to proposal|in *48 hours*" />
        <div className="proc" ref={ref}>
          <div className="proc-line"><motion.i style={{ scaleX: scrollYProgress }} /></div>
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.title} className="proc-step" delay={i * 0.12}>
              <div className="bubble"><Icon name={s.icon} size={30} /><small>{i + 1}</small></div>
              <h3 className="h3">{s.title}</h3>
              <p>{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function RequestConsultation() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [more, setMore] = useState(false);
  const [status, setStatus] = useState('idle');
  const [office, setOffice] = useState(CONTACT);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_BASE}/office-info`, { timeout: 8000 })
      .then((r) => { if (alive && r.data?.data) setOffice((o) => ({ ...o, ...Object.fromEntries(Object.entries(r.data.data).filter(([, v]) => v !== null && v !== '')) })); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const mapSrc = useMemo(() => `https://maps.google.com/maps?q=${office.lat},${office.lng}&z=16&output=embed`, [office.lat, office.lng]);
  const phoneHref = `tel:${String(office.phone).replace(/[^\d+]/g, '')}`;

  // progress: 3 required + details + message
  const steps = [
    form.name.trim() && EMAIL_RE.test(form.email) && form.phone.replace(/\D/g, '').length >= 10,
    form.projectType || form.projectLocation || form.projectScale || form.timeline || form.company,
    form.message.trim().length > 10,
  ];

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;
    setStatus('sending');
    try {
      await axios.post(`${API_BASE}/enquiry`, form);
      setStatus('done');
      setForm(EMPTY);
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) setErrors(Object.fromEntries(Object.entries(apiErrors).map(([k, m]) => [k, m[0]])));
      setStatus('error');
    }
  };

  const input = (name, label, { type = 'text', full, optional, auto } = {}) => (
    <div className={`fl${full ? ' full' : ''}`}>
      <input id={`f-${name}`} name={name} type={type} value={form[name]} onChange={onChange} placeholder=" " autoComplete={auto}
        className={`input${errors[name] ? ' has-error' : ''}`} />
      <label htmlFor={`f-${name}`}>{label}{optional && <span className="opt">(optional)</span>}</label>
      {errors[name] && <motion.span className="field-error" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>{errors[name]}</motion.span>}
    </div>
  );
  const select = (name, label, options) => (
    <div className="fl">
      <select id={`f-${name}`} name={name} value={form[name]} onChange={onChange} className={`select${form[name] ? ' filled' : ''}`}>
        <option value="" />
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <label htmlFor={`f-${name}`}>{label}<span className="opt">(optional)</span></label>
    </div>
  );

  return (
    <>
      <PageHero
        title="Let's build something|*exceptional*"
        lead="Share your vision — we'll deliver the plan. Our senior PMC team responds within 24 hours, with a tailored proposal in 48."
        images={['/projects/eeshwaran-residence-2.webp', '/projects/grand-vista-1-sm.webp']}>
        <ArrowButton href="#enquiry">Start your enquiry</ArrowButton>
        <ArrowButton href={phoneHref} variant="outline" magnetic={false}>Call us now</ArrowButton>
        <ol className="how-steps" aria-label="How to request a consultation">
          {[['Fill the 1-minute form', 'Just name, email & phone'], ['We call you', 'Within 24 hours'], ['Get your proposal', 'Tailored, in 48 hours']].map(([t, d], i) => (
            <motion.li key={t} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.12, duration: 0.6, ease: EASE }}>
              <b>{i + 1}</b><span><strong>{t}</strong><small>{d}</small></span>
            </motion.li>
          ))}
        </ol>
        <div className="quick-contact">
          <a href={phoneHref}><span><Icon name="phone" size={16} /></span>{office.phone}</a>
          <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer"><span className="wa"><WhatsAppIcon size={16} /></span>WhatsApp</a>
          <a href={`mailto:${office.email}`}><span><Icon name="mail" size={16} /></span>{office.email}</a>
        </div>
      </PageHero>

      <section className="section" id="enquiry" style={{ scrollMarginTop: 90, paddingTop: 56 }}>
        <div className="container">
          <Reveal className="contact-shell">
          <div className="form-card">
            <AnimatePresence mode="wait">
              {status === 'done' ? (
                <motion.div key="ok" className="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                  <SuccessCheck />
                  <h2 className="h2" style={{ fontSize: '2rem', marginBottom: 12 }}>Enquiry received!</h2>
                  <p className="lead" style={{ margin: '0 auto 28px' }}>Our senior PMC team will review your brief and respond within 24 hours.</p>
                  <button className="btn btn-outline" onClick={() => setStatus('idle')}>Send another enquiry</button>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={onSubmit} noValidate exit={{ opacity: 0, y: -20 }}>
                  <span className="kicker"><b>01</b>Project brief</span>
                  <h2 className="h2" style={{ fontSize: 'clamp(1.7rem,3vw,2.3rem)', marginBottom: 10 }}>Tell us about your project</h2>
                  <p className="muted" style={{ marginBottom: 24 }}>Only <strong>name, email &amp; phone</strong> are required — fill them and press <strong>Submit enquiry</strong>. Everything else is optional.</p>
                  <div className="steps-bar">
                    {steps.map((done, i) => (
                      <div key={i} className={`sb-step${done ? ' done' : ''}`}>
                        <i><motion.b initial={{ scaleX: 0 }} animate={{ scaleX: done ? 1 : 0 }} transition={{ duration: 0.6, ease: EASE }} /></i>
                        <em>{done ? '✓ ' : `${i + 1}. `}{['Your details', 'Project details', 'Your message'][i]}{i > 0 && !done ? ' (optional)' : ''}</em>
                      </div>
                    ))}
                  </div>
                  <div className="form-grid">
                    {input('name', 'Full name', { full: true, auto: 'name' })}
                    {input('email', 'Email address', { type: 'email', auto: 'email' })}
                    {input('phone', 'Contact number', { type: 'tel', auto: 'tel' })}
                    <button type="button" className={`more-btn${more ? ' open' : ''}`} onClick={() => setMore((s) => !s)} aria-expanded={more}>
                      {more ? 'Hide project details' : 'Add project details (optional)'} <Icon name="chevronDown" size={18} />
                    </button>
                    <AnimatePresence initial={false}>
                      {more && (
                        <motion.div className="full" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} style={{ overflow: 'hidden' }}>
                          <div className="form-grid" style={{ paddingTop: 2 }}>
                            {input('company', 'Company', { full: true, optional: true, auto: 'organization' })}
                            {select('projectType', 'Project type', FORM_OPTIONS.projectType)}
                            {input('projectLocation', 'Project location', { optional: true })}
                            {select('projectScale', 'Project scale', FORM_OPTIONS.projectScale)}
                            {select('timeline', 'Start timeline', FORM_OPTIONS.timeline)}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="fl full">
                      <textarea id="f-message" name="message" rows={4} value={form.message} onChange={onChange} className="textarea" maxLength={5000} placeholder=" " />
                      <label htmlFor="f-message">Tell us about your project<span className="opt">(optional)</span></label>
                    </div>
                    {status === 'error' && !Object.keys(errors).some((k) => errors[k]) && (
                      <div className="alert alert-error full">Submission failed — please try again, or call us on {office.phone}.</div>
                    )}
                    <div className="full">
                      <button type="submit" className="btn btn-primary btn-block" disabled={status === 'sending'}>
                        {status === 'sending' ? <><span className="spinner" /> Sending…</> : <>Submit enquiry <span className="arrow"><Icon name="send" size={14} /></span></>}
                      </button>
                    </div>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          <aside className="contact-panel">
            <div className="cp-deco" aria-hidden="true" />
            <span className="kicker" style={{ color: '#BFDBFE' }}><b style={{ color: '#93C5FD' }}>02</b>Prefer to talk?</span>
            <h3>Speak directly with our senior PMC team</h3>
            <p>Every call is picked up personally — no call centres, no bots.</p>
            <div className="cp-badges">
              <span><strong>24h</strong>First reply</span>
              <span><strong>48h</strong>Tailored proposal</span>
            </div>
            <Stagger gap={0.08} className="cp-rows">
              {[
                ['pin', 'Office', `${office.address}, ${office.city} – ${office.pincode}`],
                ['phone', 'Phone', office.phone, phoneHref],
                ['mail', 'Email', office.email, `mailto:${office.email}`],
                ['clock', 'Hours', office.hours],
              ].map(([ic, label, val, href]) => (
                <StaggerItem key={label} className="cp-row">
                  <span className="ico"><Icon name={ic} size={18} /></span>
                  <div><small>{label}</small>{href ? <a href={href}>{val}</a> : <span>{val}</span>}</div>
                </StaggerItem>
              ))}
            </Stagger>
            <div className="cp-actions">
              <a href={phoneHref} className="btn btn-white">Call now <span className="arrow"><Icon name="phone" size={14} /></span></a>
              <a href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hello Blue Block PMC! I'd like to discuss a project.")}`} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-light"><WhatsAppIcon size={18} />WhatsApp</a>
            </div>
          </aside>
          </Reveal>
        </div>
      </section>

      <Process />

      <section className="section">
        <div className="container">
          <SectionHead num="04" kicker="Visit us" title="Find us in|*Coimbatore*" />
          <Reveal from="zoom" className="map-card">
            <iframe title="Blue Block PMC location" src={mapSrc} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            <div className="map-info">
              <span className="ico"><Icon name="pin" size={20} /></span>
              <div>
                <strong>Blue Block PMC</strong>
                <span>{office.address}, {office.city} – {office.pincode}, {office.state}</span>
                <span className="muted">{office.hours}</span>
              </div>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${office.lat},${office.lng}`} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Get directions <span className="arrow"><Icon name="external" size={13} /></span></a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
